import MainView from "views/MainView";
import { useState, useEffect, useRef } from "react";
import {
  commandList,
  ThirdPartyService,
  _H5Event,
  handleFromAppParams,
  PLATFORM_CONFIGS,
} from "request";
import { useLocation } from "react-router-dom";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import { Spinner } from "components";
import { CommandProduct, CommandTab, CommandCard } from "./components";
import { SupportedLocales } from "views/IntlContext";

import "swiper/css";

interface IProductInfo {
  productName: string;
  picture: string;
  productType: string;
}

export interface ICommand {
  id: number;
  productId: number;
  type: number;
  title: string;
  command: string;
  updateTime: number;
  createTime: number;
  delFlag: boolean;
}

export const VoiceCommand = () => {
  const location = useLocation();
  const { state } = location;

  const [tabName, setTabName] = useState<ThirdPartyService>(
    PLATFORM_CONFIGS.alexa.enabled
      ? ThirdPartyService.alexa
      : ThirdPartyService.googleAssistant
  );
  const [swiperHeight, setSwiperHeight] = useState<number>(200);

  const productInfo = useRef<IProductInfo | undefined>(undefined);
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const swiperRef = useRef<any>(null);
  const [alexaCommands, setAlexaCommands] = useState<ICommand[]>([]);
  const [googleCommands, setGoogleCommands] = useState<ICommand[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const onGetCommondInfo = async ({
    locale,
    token,
    productId,
  }: {
    productId: string;
    locale: SupportedLocales;
    token: string;
  }) => {
    try {
      const response: any = await commandList({
        lang: locale,
        token,
        productId,
      });
      setIsLoading(false);
      if (response?.code === "200") {
        const commands = response?.result as ICommand[];

        const { alexaCommands, googleCommands } = commands.reduce(
          (acc, command) => {
            if (command.type === 1) {
              acc.alexaCommands.push(command);
            } else if (command.type === 2) {
              acc.googleCommands.push(command);
            }
            return acc;
          },
          { alexaCommands: [] as ICommand[], googleCommands: [] as ICommand[] }
        );

        setAlexaCommands(alexaCommands);
        setGoogleCommands(googleCommands);
      }
    } catch (err) {
      setIsLoading(false);
      console.log(err);
    }
  };
  // 处理 state 存在时的逻辑
  const handleStateExists = () => {
    const { productName, picture, productType, locale, token, productId } =
      state;
    productInfo.current = {
      productName,
      picture,
      productType,
    };
    onGetCommondInfo({ locale, token, productId });
  };

  // 处理 state 不存在时的逻辑
  const handleStateNotExists = () => {
    let datas = {
      event: "GetDeviceInfo",
      pageName: "commands",
      callback: (responseStr: any) => {
        const { productName, picture, productType, locale, token, productId } =
          handleFromAppParams(responseStr);
        productInfo.current = {
          productName,
          picture,
          productType,
        };
        onGetCommondInfo({ locale, token, productId });
      },
    };
    _H5Event(datas);
  };

  const onSlideChange = (swiper: SwiperClass) => {
    const activeIndex = swiper.activeIndex;
    if (activeIndex === 0) setTabName(ThirdPartyService.alexa);
    else if (activeIndex === 1) setTabName(ThirdPartyService.googleAssistant);
  };

  const onTabChange = (tabname: ThirdPartyService) => {
    setTabName(tabname);
    if (swiperRef.current) {
      if (tabname === ThirdPartyService.alexa) {
        swiperRef.current.slideTo(0);
      } else {
        swiperRef.current.slideTo(1);
      }
    }
  };

  useEffect(() => {
    setIsLoading(true);
    if (state) {
      handleStateExists();
    } else {
      handleStateNotExists();
    }
  }, [state]);

  useEffect(() => {
    if (nodeRef.current) {
      const windowHeight = window.innerHeight;
      const pageHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight
      );
      const nodeTop = nodeRef.current.getBoundingClientRect().top;
      setSwiperHeight(Math.max(pageHeight, windowHeight) - nodeTop - 8);
    }
  }, [alexaCommands, googleCommands, nodeRef.current]);

  if (isLoading) {
    return (
      <Spinner
        extraSmall
        className={{ "mt-12": true, "border-gray999": true }}
      />
    );
  }

  return (
    <MainView backgroundClassName="bg-page dark:bg-black1b1f1e pt-3">
      <div className="bg-white dark:bg-gray323434">
        <CommandProduct
          imageUrl={productInfo.current?.picture}
          productName={productInfo.current?.productName}
        />
        <div className="flex items-center pb-1">
          {PLATFORM_CONFIGS.alexa.enabled && (
            <CommandTab
              tabName={ThirdPartyService.alexa}
              activeTabName={tabName}
              onClick={() => onTabChange(ThirdPartyService.alexa)}
            />
          )}

          {PLATFORM_CONFIGS.googleAssistant.enabled && (
            <CommandTab
              tabName={ThirdPartyService.googleAssistant}
              activeTabName={tabName}
              onClick={() => onTabChange(ThirdPartyService.googleAssistant)}
            />
          )}
        </div>
      </div>
      <div className="pt-1 pb-1" ref={nodeRef}>
        <Swiper
          style={{
            height: `${swiperHeight}px`,
            overflowY: "auto",
            paddingBottom: "4px",
          }}
          onSlideChange={onSlideChange}
          onSwiper={(swiper: SwiperClass) => (swiperRef.current = swiper)}
        >
          {PLATFORM_CONFIGS.alexa.enabled && (
            <SwiperSlide style={{ height: `${swiperHeight}px` }}>
              <CommandCard commands={alexaCommands} />
            </SwiperSlide>
          )}

          {PLATFORM_CONFIGS.googleAssistant.enabled && (
            <SwiperSlide style={{ height: `${swiperHeight}px` }}>
              <CommandCard commands={googleCommands} />
            </SwiperSlide>
          )}
        </Swiper>
      </div>
    </MainView>
  );
};
